import React, { useState, useEffect } from 'react';
import { Search, Filter, Eye, Edit, CheckCircle, XCircle, Truck } from 'lucide-react';
import api from '../../services/api';
import toast from 'react-hot-toast';

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    try {
      const response = await api.get('/orders/admin/all');
      setOrders(response.data.orders || []);
    } catch (error) {
      console.error('Siparişler yüklenemedi:', error);
      toast.error('Siparişler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (orderId, newStatus) => {
    try {
      await api.put(`/orders/${orderId}/status`, { status: newStatus });
      toast.success('Sipariş durumu güncellendi');
      loadOrders();
      setShowModal(false);
      setSelectedOrder(null);
    } catch (error) {
      console.error('Durum güncellenemedi:', error);
      toast.error(error.response?.data?.message || 'Durum güncellenemedi');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'beklemede': return 'bg-yellow-100 text-yellow-800';
      case 'onaylandi': return 'bg-blue-100 text-blue-800';
      case 'hazirlaniyor': return 'bg-purple-100 text-purple-800';
      case 'kargoda': return 'bg-indigo-100 text-indigo-800';
      case 'teslim_edildi': return 'bg-green-100 text-green-800';
      case 'iptal': return 'bg-red-100 text-red-800';
      // Eski değerler için backward compatibility
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'processing': return 'bg-blue-100 text-blue-800';
      case 'shipped': return 'bg-purple-100 text-purple-800';
      case 'delivered': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'beklemede': return 'Beklemede';
      case 'onaylandi': return 'Onaylandı';
      case 'hazirlaniyor': return 'Hazırlanıyor';
      case 'kargoda': return 'Kargoda';
      case 'teslim_edildi': return 'Teslim Edildi';
      case 'iptal': return 'İptal Edildi';
      // Eski değerler için backward compatibility
      case 'pending': return 'Beklemede';
      case 'processing': return 'İşleniyor';
      case 'shipped': return 'Kargoda';
      case 'delivered': return 'Teslim Edildi';
      case 'cancelled': return 'İptal Edildi';
      default: return status || 'Bilinmeyen';
    }
  };

  const filteredOrders = orders.filter(order => {
    const matchesSearch = order.order_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         order.user?.name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !statusFilter || order.order_status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="bg-white p-4 rounded-lg shadow">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Sipariş Yönetimi</h1>
        <p className="mt-2 text-gray-600">Tüm siparişleri görüntüleyin ve yönetin</p>
      </div>

      {/* Filters */}
      <div className="bg-white p-6 rounded-lg shadow mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Sipariş ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">Tüm Durumlar</option>
            <option value="beklemede">Beklemede</option>
            <option value="onaylandi">Onaylandı</option>
            <option value="hazirlaniyor">Hazırlanıyor</option>
            <option value="kargoda">Kargoda</option>
            <option value="teslim_edildi">Teslim Edildi</option>
            <option value="iptal">İptal Edildi</option>
          </select>

          <button
            onClick={loadOrders}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center"
          >
            <Filter className="h-4 w-4 mr-2" />
            Filtrele
          </button>
        </div>
      </div>

      {/* Orders Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Sipariş No
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Müşteri
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tutar
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Durum
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tarih
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  İşlemler
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredOrders.map((order) => (
                <tr key={order.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    #{order.order_number}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {order.user?.name || order.shipping_name || 'Misafir Kullanıcı'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    ₺{parseFloat(order.total || order.total_amount || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(order.order_status)}`}>
                      {getStatusText(order.order_status)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(order.createdAt).toLocaleDateString('tr-TR')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => {
                        setSelectedOrder(order);
                        setShowModal(true);
                      }}
                      className="text-blue-600 hover:text-blue-900 mr-3"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => {
                        setSelectedOrder(order);
                        setShowDetailModal(true);
                      }}
                      className="text-gray-600 hover:text-gray-900"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Status Update Modal */}
      {showModal && selectedOrder && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Sipariş Durumunu Güncelle
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Sipariş #{selectedOrder.order_number} için yeni durum seçin:
              </p>
              
              <div className="space-y-2">
                {['beklemede', 'onaylandi', 'hazirlaniyor', 'kargoda', 'teslim_edildi', 'iptal'].map((status) => (
                  <button
                    key={status}
                    onClick={() => handleStatusUpdate(selectedOrder.id, status)}
                    className={`w-full text-left px-4 py-2 rounded-lg border transition-colors ${
                      selectedOrder.order_status === status
                        ? 'border-blue-500 bg-blue-50 text-blue-700 font-semibold'
                        : 'border-gray-300 hover:bg-gray-50 hover:border-gray-400'
                    }`}
                  >
                    {getStatusText(status)}
                  </button>
                ))}
              </div>
              
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  İptal
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Order Detail Modal */}
      {showDetailModal && selectedOrder && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-2xl font-bold text-gray-900">
                  Sipariş Detayları - #{selectedOrder.order_number}
                </h3>
                <button
                  onClick={() => {
                    setShowDetailModal(false);
                    setSelectedOrder(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ×
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Sol Taraf - Müşteri ve Teslimat Bilgileri */}
                <div className="space-y-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-3">
                      Müşteri Bilgileri
                      {!selectedOrder.user && (
                        <span className="ml-2 text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded-full">Misafir</span>
                      )}
                    </h4>
                    <div className="space-y-2 text-sm">
                      <p>
                        <span className="font-medium">Ad Soyad:</span> 
                        <span className="ml-2">{selectedOrder.user?.name || selectedOrder.shipping_name || 'Bilinmeyen'}</span>
                      </p>
                      <p>
                        <span className="font-medium">E-posta:</span> 
                        <span className="ml-2">{selectedOrder.user?.email || selectedOrder.guest_email || '-'}</span>
                      </p>
                      <p>
                        <span className="font-medium">Telefon:</span> 
                        <span className="ml-2">{selectedOrder.user?.phone || selectedOrder.shipping_phone || '-'}</span>
                      </p>
                      {!selectedOrder.user && selectedOrder.guest_email && (
                        <p className="text-xs text-gray-500 italic mt-2">
                          Bu sipariş misafir kullanıcı tarafından verilmiştir
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-3">Teslimat Adresi</h4>
                    <div className="space-y-2 text-sm">
                      <p><span className="font-medium">Adres:</span> {selectedOrder.shipping_address || '-'}</p>
                      <p><span className="font-medium">Şehir:</span> {selectedOrder.shipping_city || '-'}</p>
                      <p><span className="font-medium">İlçe:</span> {selectedOrder.shipping_state || '-'}</p>
                      {selectedOrder.shipping_zip_code && (
                        <p><span className="font-medium">Posta Kodu:</span> {selectedOrder.shipping_zip_code}</p>
                      )}
                      <p><span className="font-medium">Telefon:</span> {selectedOrder.shipping_phone || '-'}</p>
                    </div>
                  </div>
                </div>

                {/* Sağ Taraf - Sipariş ve Ödeme Bilgileri */}
                <div className="space-y-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-3">Sipariş Bilgileri</h4>
                    <div className="space-y-2 text-sm">
                      <p><span className="font-medium">Sipariş No:</span> #{selectedOrder.order_number}</p>
                      <p><span className="font-medium">Sipariş Tarihi:</span> {new Date(selectedOrder.created_at || selectedOrder.createdAt).toLocaleString('tr-TR')}</p>
                      <p><span className="font-medium">Sipariş Durumu:</span> 
                        <span className={`ml-2 px-2 py-1 rounded-full text-xs ${getStatusColor(selectedOrder.order_status)}`}>
                          {getStatusText(selectedOrder.order_status)}
                        </span>
                      </p>
                      <p><span className="font-medium">Ödeme Durumu:</span> 
                        <span className={`ml-2 px-2 py-1 rounded-full text-xs ${
                          selectedOrder.payment_status === 'odendi' ? 'bg-green-100 text-green-800' :
                          selectedOrder.payment_status === 'iptal' ? 'bg-red-100 text-red-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {selectedOrder.payment_status === 'odendi' ? 'Ödendi' :
                           selectedOrder.payment_status === 'iptal' ? 'İptal' : 'Beklemede'}
                        </span>
                      </p>
                      {selectedOrder.payment_id && (
                        <p><span className="font-medium">Ödeme ID:</span> {selectedOrder.payment_id}</p>
                      )}
                      {selectedOrder.tracking_number && (
                        <p><span className="font-medium">Takip No:</span> {selectedOrder.tracking_number}</p>
                      )}
                    </div>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-3">Tutar Bilgileri</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span>Ara Toplam:</span>
                        <span>₺{parseFloat(selectedOrder.subtotal || selectedOrder.subtotal_amount || 0).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Kargo:</span>
                        <span>{parseFloat(selectedOrder.shipping_cost || 0) > 0 ? `₺${parseFloat(selectedOrder.shipping_cost || 0).toFixed(2)}` : 'Ücretsiz'}</span>
                      </div>
                      <div className="flex justify-between font-bold text-lg border-t pt-2">
                        <span>Toplam:</span>
                        <span>₺{parseFloat(selectedOrder.total || selectedOrder.total_amount || 0).toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Ürünler */}
              {selectedOrder.items && selectedOrder.items.length > 0 && (
                <div className="mt-6 bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-3">Sipariş Edilen Ürünler</h4>
                  <div className="space-y-3">
                    {selectedOrder.items.map((item, index) => {
                      // Görsel yolu kontrolü - object veya string olabilir
                      let productImage = item.product?.images?.[0] || item.image || null;
                      if (productImage) {
                        if (typeof productImage === 'object' && productImage?.url) {
                          productImage = productImage.url;
                        } else if (typeof productImage !== 'string') {
                          productImage = null;
                        }
                      }
                      
                      return (
                      <div key={index} className="bg-white p-3 rounded-lg flex items-center space-x-4">
                        {productImage && (
                          <img
                            src={typeof productImage === 'string' && productImage.startsWith('http') 
                              ? productImage 
                              : typeof productImage === 'string' && productImage.startsWith('/')
                                ? productImage
                                : `/uploads/${productImage}`}
                            alt={item.product?.name || item.name}
                            className="w-16 h-16 object-cover rounded"
                            onError={(e) => {
                              e.target.src = '/images/placeholder.jpg';
                            }}
                          />
                        )}
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{item.product?.name || item.name || 'Ürün'}</p>
                          <p className="text-sm text-gray-600">Adet: {item.quantity || 1}</p>
                          {item.product?.sku && (
                            <p className="text-xs text-gray-500">SKU: {item.product.sku}</p>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-gray-900">
                            ₺{parseFloat((item.price || 0) * (item.quantity || 1)).toFixed(2)}
                          </p>
                          {item.quantity > 1 && (
                            <p className="text-xs text-gray-500">₺{parseFloat(item.price || 0).toFixed(2)} / adet</p>
                          )}
                        </div>
                      </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {selectedOrder.notes && (
                <div className="mt-4 bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Sipariş Notları</h4>
                  <p className="text-sm text-gray-700">{selectedOrder.notes}</p>
                </div>
              )}

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowDetailModal(false);
                    setShowModal(true);
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Durum Güncelle
                </button>
                <button
                  onClick={() => {
                    setShowDetailModal(false);
                    setSelectedOrder(null);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Kapat
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminOrders;